import { Box, Container, Typography, Card, CardActionArea, CardContent, alpha } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SearchIcon from '@mui/icons-material/Search';

const pulseKeyframes = `
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.08); }
    100% { transform: scale(1); }
  }
  @keyframes floatUp {
    from { opacity: 0; transform: translateY(30px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

export default function ChoicePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const cards = [
    {
      key: 'donor',
      icon: <FavoriteIcon sx={{ fontSize: 64 }} />,
      title: t('choice.donor_btn'),
      sub: t('choice.donor_sub'),
      path: '/register',
      gradient: 'linear-gradient(135deg, #c62828 0%, #7f0f0f 100%)',
      delay: '0ms',
    },
    {
      key: 'seeker',
      icon: <SearchIcon sx={{ fontSize: 64 }} />,
      title: t('choice.seeker_btn'),
      sub: t('choice.seeker_sub'),
      path: '/search',
      gradient: 'linear-gradient(135deg, #37474f 0%, #102027 100%)',
      delay: '120ms',
    },
  ];

  return (
    <>
      <style>{pulseKeyframes}</style>
      <Box
        sx={{
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'radial-gradient(ellipse at 60% 30%, #fce8e8 0%, #fafafa 70%)',
          py: 6,
        }}
      >
        <Container maxWidth="md">
          {/* Hero text */}
          <Box textAlign="center" mb={6} sx={{ animation: 'floatUp 0.6s ease both' }}>
            <FavoriteIcon
              color="primary"
              sx={{ fontSize: 72, animation: 'pulse 2s ease-in-out infinite', mb: 2 }}
            />
            <Typography variant="h2" fontWeight={900} color="primary.main" gutterBottom>
              {t('choice.title')}
            </Typography>
            <Typography variant="h5" color="text.secondary">
              {t('choice.subtitle')}
            </Typography>
          </Box>

          {/* Choice cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 3,
            }}
          >
            {cards.map((card) => (
              <Card
                key={card.key}
                sx={{
                  animation: `floatUp 0.6s ease ${card.delay} both`,
                  borderRadius: 4,
                  overflow: 'hidden',
                }}
              >
                <CardActionArea
                  onClick={() => navigate(card.path)}
                  sx={{
                    p: 4,
                    background: card.gradient,
                    color: 'white',
                    textAlign: 'center',
                    minHeight: 220,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      filter: 'brightness(1.1)',
                      transform: 'scale(1.02)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ color: alpha('#fff', 0.9), mb: 1 }}>{card.icon}</Box>
                    <Typography variant="h4" fontWeight={800} color="white" gutterBottom>
                      {card.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: alpha('#fff', 0.85) }}>
                      {card.sub}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>
    </>
  );
}
